import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, PlayCircle, RefreshCw, Truck, Package, PackageCheck, Search } from "lucide-react";

/**
 * AdminShipmentsTab — launch-day monitor + milestone simulator.
 *
 * Table of every warehouse_order enriched with:
 *   - recipient email
 *   - state (labeled/in-transit/delivered)
 *   - green/gray checkmarks for each milestone email that has fired
 *   - "Simulate" button to fire selected milestone emails against a REAL order
 */

const STAGES = [
  { key: "transit", label: "Just left the ranch", icon: Truck, flag: "notified_transit_at" },
  { key: "landing_tomorrow", label: "Landing tomorrow", icon: Package, flag: "notified_landing_tomorrow_at" },
  { key: "delivered", label: "Delivered (urgent)", icon: PackageCheck, flag: "notified_delivered_at" },
];

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminShipmentsTab() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [simTarget, setSimTarget] = useState(null); // wh_order object
  const [selectedStages, setSelectedStages] = useState({ transit: true, landing_tomorrow: true, delivered: true });
  const [resetFlags, setResetFlags] = useState(true);
  const [photoUrl, setPhotoUrl] = useState("https://images.unsplash.com/photo-1595246140361-c8b7cca8b3fa?w=800&auto=format&fit=crop");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    api.get("/admin/shipments")
      .then(r => setRows(r.data?.shipments || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = (rows || []).filter(r => {
    if (!q) return true;
    const n = q.toLowerCase();
    return (r.id || "").toLowerCase().includes(n)
      || (r.drop_slug || "").toLowerCase().includes(n)
      || (r.user_email || "").toLowerCase().includes(n)
      || (r.tracking_number || "").toLowerCase().includes(n);
  });

  const runSimulation = async () => {
    if (!simTarget) return;
    const stages = STAGES.filter(s => selectedStages[s.key]).map(s => s.key);
    if (stages.length === 0) {
      toast.error("Pick at least one stage to fire");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/admin/shipments/${simTarget.id}/simulate`, {
        stages,
        delivery_photo_url: selectedStages.delivered ? (photoUrl || null) : null,
        dry_run: false,
        reset_flags: resetFlags,
      });
      const sent = res.data?.sent || [];
      const errs = res.data?.errors || [];
      if (sent.length) {
        toast.success(`Fired: ${sent.join(", ")} → ${simTarget.user_email}`);
      }
      if (errs.length) {
        toast.error(`Errors on: ${errs.map(e => e.stage).join(", ")}`);
        console.error("Simulate errors:", errs);
      }
      setSimTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    } finally {
      setBusy(false);
    }
  };

  if (rows === null) {
    return <div className="p-10 text-[var(--ff-text-muted)]" data-testid="admin-shipments-loading">Loading shipments…</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-shipments-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-3xl">Shipments monitor</h2>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">
            {filtered.length} of {rows.length} warehouse orders · milestone emails checkmarked as they fire
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)]"/>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search id, drop, email, tracking"
              className="input-base pl-9 text-sm w-72"
              data-testid="admin-shipments-search"
            />
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ff-border)] hover:border-[var(--ff-primary)] px-3 py-1.5 text-xs font-medium"
            data-testid="admin-shipments-refresh"
          >
            <RefreshCw size={13}/> Refresh
          </button>
        </div>
      </div>

      <div className="card-flat overflow-x-auto" data-testid="admin-shipments-table-wrap">
        <table className="w-full text-sm" data-testid="admin-shipments-table">
          <thead className="border-b border-[var(--ff-border)] text-[var(--ff-text-muted)] uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Recipient</th>
              <th className="text-left px-4 py-3">State · Tracking</th>
              <th className="text-center px-4 py-3">Ranch</th>
              <th className="text-center px-4 py-3">Landing</th>
              <th className="text-center px-4 py-3">Delivered</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--ff-border)]">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-[var(--ff-text-muted)]">
                  No shipments yet — real orders will populate this on launch day.
                </td>
              </tr>
            )}
            {filtered.map(r => (
              <tr key={r.id} data-testid={`admin-shipment-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs text-[var(--ff-text-muted)]">{r.id.slice(0, 12)}…</div>
                  <div className="font-medium mt-0.5">{r.drop_slug || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.user_name || "—"}</div>
                  <div className="text-xs text-[var(--ff-text-muted)]">{r.user_email || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs uppercase tracking-wider text-[var(--ff-primary)] font-semibold">
                    {r.state || "—"}
                  </div>
                  {r.tracking_number && (
                    <div className="font-mono text-xs mt-0.5">{r.tracking_number} · {(r.carrier || "").toUpperCase()}</div>
                  )}
                </td>
                {STAGES.map(s => {
                  const stamped = r[s.flag];
                  return (
                    <td key={s.key} className="px-4 py-3 text-center" data-testid={`admin-shipment-${r.id}-${s.key}`}>
                      {stamped ? (
                        <div className="inline-flex flex-col items-center">
                          <CheckCircle2 size={18} className="text-emerald-600"/>
                          <span className="text-[10px] text-[var(--ff-text-muted)] mt-1">{fmtDate(stamped)}</span>
                        </div>
                      ) : (
                        <Circle size={16} className="text-[var(--ff-text-muted)] inline"/>
                      )}
                    </td>
                  );
                })}
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => {
                      setSimTarget(r);
                      setSelectedStages({ transit: true, landing_tomorrow: true, delivered: true });
                      setResetFlags(true);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ff-primary)] text-[var(--ff-primary)] hover:bg-[var(--ff-primary)] hover:text-white transition-colors px-3 py-1.5 text-xs font-semibold"
                    data-testid={`admin-shipment-simulate-btn-${r.id}`}
                  >
                    <PlayCircle size={13}/> Simulate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Simulate modal */}
      {simTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
          data-testid="admin-shipment-simulate-modal"
          onClick={() => !busy && setSimTarget(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-display text-2xl text-[var(--ff-primary)]">Fire milestone emails</h3>
            <p className="text-sm text-[var(--ff-text-secondary)] mt-1">
              To <strong>{simTarget.user_email}</strong> for order
              <span className="font-mono text-xs ml-1">{simTarget.id.slice(0, 12)}…</span>
            </p>

            <div className="mt-6 space-y-3">
              {STAGES.map(s => {
                const Icon = s.icon;
                return (
                  <label
                    key={s.key}
                    className="flex items-center gap-3 p-3 border border-[var(--ff-border)] rounded-xl cursor-pointer hover:bg-[var(--ff-accent-kraft)]/10"
                    data-testid={`admin-shipment-stage-${s.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedStages[s.key]}
                      onChange={e => setSelectedStages(prev => ({ ...prev, [s.key]: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <Icon size={16} className="text-[var(--ff-primary)]"/>
                    <span className="text-sm font-medium">{s.label}</span>
                  </label>
                );
              })}
            </div>

            {selectedStages.delivered && (
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest text-[var(--ff-text-muted)]">
                  Delivery photo URL (optional)
                </label>
                <input
                  value={photoUrl}
                  onChange={e => setPhotoUrl(e.target.value)}
                  placeholder="https://…"
                  className="input-base text-sm w-full mt-1"
                  data-testid="admin-shipment-photo-url"
                />
              </div>
            )}

            <label className="mt-5 flex items-center gap-2 text-xs text-[var(--ff-text-secondary)]">
              <input
                type="checkbox"
                checked={resetFlags}
                onChange={e => setResetFlags(e.target.checked)}
                data-testid="admin-shipment-reset-flags"
              />
              Reset idempotency flags first (so already-fired emails will re-send)
            </label>

            <div className="mt-8 flex justify-end gap-2">
              <button
                onClick={() => setSimTarget(null)}
                disabled={busy}
                className="px-4 py-2 text-sm text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]"
                data-testid="admin-shipment-simulate-cancel"
              >
                Cancel
              </button>
              <button
                onClick={runSimulation}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--ff-primary)] text-white px-5 py-2 text-sm font-semibold disabled:opacity-50"
                data-testid="admin-shipment-simulate-confirm"
              >
                {busy ? "Firing…" : "Fire emails"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
